import { batch } from "react-redux";
import { setQrCode } from "../slices/accessSlice";
import { setAppState } from "../slices/appStateSlice";
import { applyRefillInventory, setDishTypes, setDishTypesLighting, syncDishPositions } from "../slices/dishesSlice";
import { setDoorLockState, setDoorState, setLockState } from "../slices/doorSlice";
import { setFridgeState } from "../slices/fridgeInfoSlice";
import { setLightingMode } from "../slices/fridgeLightingSlice";
import { isActiveOrderSlot, isActiveRefillSlot, setTransaction } from "../slices/transactionSlice";
import type { AppDispatch, RootState } from "../store";
import { type DoorState, type Refill, RefillTransactionMode, TransactionState } from "../types";
import { AppState, type LocalDishType, type LocalFridgeState } from "../types/mainTypes";
import { logToSentry } from "../utils/logger";
import { computeEffectiveRefillLedger } from "../utils/refillLedger";
import { getRefillTransitions } from "../utils/refillTransitions";
import { isLightingEchoFrame } from "../utils/utils";

export type ApplyFridgeFrameParams = {
	// The FRIDGE_SUBSCRIPTION frame with `__typename` already stripped (a SPARSE delta — only changed
	// fields are set, the rest are null; NEVER a full authoritative snapshot).
	cleanedData: LocalFridgeState;
	dispatch: AppDispatch;
	// Read synchronously between dispatches (dispatch is synchronous, so getState() is never stale even
	// when rapid pushes arrive before React re-renders — the reason the caller passes the store, not a
	// closed-over selector value).
	getState: () => RootState;
	trackDoorStateChange: (doorState: DoorState) => void;
	trackRefillStarted: (refill: Refill) => void;
	trackRefillFinished: (refill: Refill) => void;
	getUnknownDishTypes: (dishTypes: Pick<LocalDishType, "id">[], refillId?: string) => void;
	idleTimer: { start: () => void };
	lastTrackedDoorStateRef: { current: DoorState | null };
	// Unknown type.ids from inventory.added already fetch-attempted this refill pass. Guards the
	// cumulative ledger from re-firing FETCH_UNKNOWN_DISH_TYPE at frame rate while a fetch is in
	// flight or after it failed (one attempt per type per pass; a later pass or any non-refill
	// frame carrying the type retries/merges it). Cleared on isNewRefill.
	refillTypeFetchAttemptsRef: { current: Set<string> };
	fridgeId: string;
};

const applyFridgeState = ({
	cleanedData,
	dispatch,
}: Pick<ApplyFridgeFrameParams, "cleanedData" | "dispatch">): void => {
	if (cleanedData.state != null) {
		dispatch(setFridgeState(cleanedData.state));
	}
};

const applyAccess = ({ cleanedData, dispatch }: Pick<ApplyFridgeFrameParams, "cleanedData" | "dispatch">): void => {
	if (cleanedData.access?.qrCode?.code) {
		dispatch(setQrCode(cleanedData.access.qrCode.code));
	}
};

// The door and its lock: at most one dispatch per frame, and the door state tracked when it differs from the
// last one tracked.
const applyDoor = ({
	cleanedData,
	dispatch,
	trackDoorStateChange,
	lastTrackedDoorStateRef,
}: Pick<
	ApplyFridgeFrameParams,
	"cleanedData" | "dispatch" | "trackDoorStateChange" | "lastTrackedDoorStateRef"
>): void => {
	const hasDoorStateChange = cleanedData.door?.lock?.state;

	if (hasDoorStateChange) {
		if (cleanedData.door.state && cleanedData.door.state !== lastTrackedDoorStateRef.current) {
			trackDoorStateChange(cleanedData.door.state);
			lastTrackedDoorStateRef.current = cleanedData.door.state;
		}
		dispatch(setDoorLockState(cleanedData.door));
	} else if (cleanedData?.door?.state) {
		if (cleanedData.door.state !== lastTrackedDoorStateRef.current) {
			trackDoorStateChange(cleanedData.door.state);
			lastTrackedDoorStateRef.current = cleanedData.door.state;
		}
		dispatch(setDoorState(cleanedData.door.state));
	} else if (cleanedData?.door?.lock?.state) {
		// Never runs: its condition is the first branch's. Stays until a bench capture of a lock-only frame decides.
		dispatch(setLockState(cleanedData.door.lock?.state));
	}
};

const applyLighting = ({ cleanedData, dispatch }: Pick<ApplyFridgeFrameParams, "cleanedData" | "dispatch">): void => {
	if (cleanedData.lighting?.mode != null) {
		dispatch(setLightingMode(cleanedData.lighting.mode));
	}
};

// The refill's inventory ledger: applies it to the dish types, and fetches each added type the FE does not
// know yet, once per pass.
const applyRefillLedger = (
	{
		dispatch,
		getState,
		getUnknownDishTypes,
		refillTypeFetchAttemptsRef,
	}: Pick<ApplyFridgeFrameParams, "dispatch" | "getState" | "getUnknownDishTypes" | "refillTypeFetchAttemptsRef">,
	prevInventory: Refill["inventory"] | null,
	nextSlot: { transaction: Refill },
): void => {
	const { effectiveRemoved, effectiveAdded } = computeEffectiveRefillLedger(
		prevInventory,
		nextSlot.transaction.inventory,
	);
	if (effectiveRemoved.length > 0 || effectiveAdded.length > 0) {
		dispatch(applyRefillInventory({ removed: effectiveRemoved, added: effectiveAdded }));
	}
	if (effectiveAdded.length > 0) {
		const knownTypeIds = new Set((getState().dishes.types ?? []).map((dishType) => dishType.id));
		const unknownTypeIds = [
			...new Set(
				effectiveAdded
					.map((dish) => dish.type?.id)
					.filter(
						(typeId): typeId is string =>
							typeId != null && !knownTypeIds.has(typeId) && !refillTypeFetchAttemptsRef.current.has(typeId),
					),
			),
		];
		if (unknownTypeIds.length > 0) {
			for (const typeId of unknownTypeIds) {
				refillTypeFetchAttemptsRef.current.add(typeId);
			}
			getUnknownDishTypes(
				unknownTypeIds.map((typeId) => ({ id: typeId })),
				nextSlot.transaction.id,
			);
		}
	}
};

// A refill holds the slot after this frame: its start, its finish (with the wake of a remote one) and its
// inventory ledger.
const applyRefill = (
	params: ApplyFridgeFrameParams,
	prevRefill: Refill | null,
	nextSlot: { transaction: Refill },
): void => {
	const {
		cleanedData,
		dispatch,
		trackRefillStarted,
		trackRefillFinished,
		idleTimer,
		refillTypeFetchAttemptsRef,
		fridgeId,
	} = params;
	const { isNewRefill, isRefillFinished } = getRefillTransitions(prevRefill, nextSlot.transaction);
	if (isNewRefill) {
		refillTypeFetchAttemptsRef.current.clear();
	}
	// Finished guard: reconnect echoes of old finished refills must not emit a spurious "started".
	if (isNewRefill && nextSlot.transaction.state !== TransactionState.Finished) {
		logToSentry(`Refill started on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
		trackRefillStarted(nextSlot.transaction);
	}
	if (isRefillFinished) {
		logToSentry(`Refill finished on fridge: ${fridgeId}, mode: ${nextSlot.transaction.mode}`, "info");
		trackRefillFinished(nextSlot.transaction);
		if (nextSlot.transaction.mode === RefillTransactionMode.Remote) {
			// The end of a remote refill counts as fresh presence (mirrors finishOrder):
			// re-arm the inactive countdown and wake appState so the fridge and screen
			// stay on for a FULL inactive timeout before the normal idle edge dims them.
			// Remote refills have no OrderStep5/finishOrder, so nothing else provides
			// this wake — without it the BE-lit fridge stayed ACTIVE forever after a
			// remote refill (HW capture 11. 6. 2026).
			idleTimer.start();
			dispatch(setAppState(AppState.ACTIVE));
		}
	}
	// JID-2303: the refill transaction's inventory is the single stock authority during a
	// refill — re-applied idempotently on every frame that actually carries the refill,
	// including the finish frame. The measured NET-cumulative/re-netting semantics (and why
	// each revocation folds into the opposite list) live on computeEffectiveRefillLedger.
	// Deliberately mode-agnostic (local == remote).
	if (cleanedData.transaction?.refills?.last) {
		const prevInventory = !isNewRefill && prevRefill ? prevRefill.inventory : null;
		applyRefillLedger(params, prevInventory, nextSlot);
	}
};

const applyTransaction = (params: ApplyFridgeFrameParams): void => {
	const { cleanedData, dispatch, getState } = params;
	const transaction = cleanedData.transaction;
	if (transaction?.orders?.last || transaction?.refills?.last) {
		const prevSlot = getState().transaction.slot;
		dispatch(
			setTransaction({
				order: transaction.orders?.last ?? null,
				refill: transaction.refills?.last ?? null,
			}),
		);
		const nextSlot = getState().transaction.slot;
		// "Door opened during this transaction" needs no reset when another transaction takes the slot:
		// doorOpenedMiddleware keys it by the transaction's id.
		if (nextSlot?.kind === "refill") {
			const prevRefill = prevSlot?.kind === "refill" ? prevSlot.transaction : null;
			applyRefill(params, prevRefill, nextSlot);
		}
	}
};

type FrameDishes = {
	hasDishTypes: LocalDishType[] | null;
	isLightingEcho: boolean;
	isLastOrderFinished: boolean;
};

// Routes the frame's dish types: a lighting echo, a frame during a running refill, or the merge/snapshot
// reconcile.
const applyDishes = (
	{
		dispatch,
		getState,
		getUnknownDishTypes,
	}: Pick<ApplyFridgeFrameParams, "dispatch" | "getState" | "getUnknownDishTypes">,
	{ hasDishTypes, isLightingEcho, isLastOrderFinished }: FrameDishes,
): void => {
	const slot = getState().transaction.slot;
	if (isLightingEcho) {
		// Lighting-only echo: update per-type lighting.mode without touching the type set or any
		// `dishes` array, so the dish types the sparse frame omits stay on the Meal List.
		dispatch(setDishTypesLighting({ dishTypes: hasDishTypes ?? [] }));
	} else if (isActiveRefillSlot(slot)) {
		// JID-2303: during an ACTIVE refill (local or remote alike) stock comes exclusively from
		// the transaction's inventory ledger (applyRefillInventory above) — dish frames are sparse
		// deltas whose absence semantics wrongly emptied the Meal List (the ticket's bug). They
		// still carry fresh per-type lighting.mode, so route them through the lighting-only update.
		// Skipping setDishTypes here also removes the per-frame mergeDishTypes churn (~290k
		// allocations per pass) and new types arrive complete via the ledger-driven unknown-type
		// fetch, so no getUnknownDishTypes call is needed either.
		if (hasDishTypes) {
			dispatch(setDishTypesLighting({ dishTypes: hasDishTypes }));
			// The frame's per-type dish list is the one place positions ARE synced during a refill
			// (the BE omits `position` on the ledger entries), so back-fill them by dish id here —
			// membership itself stays ledger-driven.
			dispatch(syncDishPositions({ dishTypes: hasDishTypes }));
		}
	} else if (hasDishTypes || isLastOrderFinished) {
		// `dishes.types` carries TWO frame semantics outside a refill. During an ACTIVE order the BE
		// streams SPARSE single-type deltas (absence = unchanged), so the reducer must merge only —
		// emptying absent types collapsed availableDishTypeIds to the one delta'd type (Meal List +
		// lighting collapse; 1.14.x regression of 4af3a56). Order-finish and browse frames send the
		// FULL authoritative snapshot (absence = the last dish was taken → drop it), so they keep
		// the destructive reconcile; when an emptied fridge finishes an order the BE sends
		// `dishes.types: null` and the `[]` fallback clears the final type. While a FINISHED refill
		// still holds the slot, no transaction is active so no stock physically changes — merge-only
		// there keeps a sparse post-finish frame from re-wiping the freshly restocked list.
		if (hasDishTypes) {
			// Scope to the refill still owning the slot (a FINISHED pass keeps its final ledger
			// there): the finish frame can carry both an unknown ledger addition and a dishes.types
			// entry for it, double-dispatching this fetch — an unscoped duplicate's fulfill would
			// bypass the ledger reconcile and merge a stale payload verbatim, undoing a revocation
			// the scoped path applied on this very frame. Order/browse fetches stay unscoped.
			getUnknownDishTypes(hasDishTypes, slot?.kind === "refill" ? slot.transaction.id : undefined);
		}
		dispatch(
			setDishTypes({
				dishTypes: hasDishTypes ?? [],
				reconcileRemovals: !(isActiveOrderSlot(slot) || slot?.kind === "refill"),
			}),
		);
	}
};

/**
 * Applies one FRIDGE_SUBSCRIPTION frame to Redux. Extracted verbatim from DeviceContext's `useSubscription`
 * onData so the sparse-delta fan-out — the seam where the 1.14.x lighting/refill regressions lived — is
 * unit-testable in isolation. Pure w.r.t. its params: it only dispatches and reads the injected store.
 */
export const applyFridgeFrame = (params: ApplyFridgeFrameParams): void => {
	const { cleanedData } = params;
	const transaction = cleanedData.transaction;
	const isLastOrderFinished = transaction?.orders?.last?.state === TransactionState.Finished;
	const hasDishTypes = cleanedData?.dishes?.types;
	// A spotlight TOGGLE echo is a SPARSE single-type frame (lighting.mode set, `dishes: null`). It
	// must NOT go through the snapshot reconcile, which would empty every still-stocked type the echo
	// omits — the master Meal List wipe on detail-open. Route it to a lighting-only update instead.
	const isLightingEcho = isLightingEchoFrame(hasDishTypes);

	// One function per section of the frame, in the order they land: the dish routing reads the slot
	// the transaction section has just written.
	batch(() => {
		applyFridgeState(params);
		applyAccess(params);
		applyDoor(params);
		applyLighting(params);
		applyTransaction(params);
		applyDishes(params, {
			hasDishTypes: hasDishTypes,
			isLightingEcho: isLightingEcho,
			isLastOrderFinished: isLastOrderFinished,
		});
	});
};
